import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Button } from 'primereact/button'
import { useMountEffect } from 'primereact/hooks'
import { assign, cloneDeep, isEmpty, max, padStart } from 'lodash'
import { CommonContext } from '../common/context/commonContext'
import {
    GoodsMoveProps,
    GoodsReturnProps,
    GoodsReturnReq,
    InventoryGoodsDetail,
    LocationArea,
    LocationDetail,
    LocationPasswordReq,
} from '../../types/inventory'
import { Divider } from 'primereact/divider'
import { CommonCode } from '../../types/common'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { useFormik } from 'formik'
import { number, object, string } from 'yup'
import { InputNumber } from 'primereact/inputnumber'
import { InputTextarea } from 'primereact/inputtextarea'
import { useTranslation } from 'react-i18next'
import { tokens } from 'locales/tokens'

const GoodsMove = (props: GoodsReturnProps) => {
    const { $toast, $http, $alert, $codes } = useContext(CommonContext)
    const { t } = useTranslation()
    const [rtrnTypCd, setRtrnTypCd] = useState<CommonCode[]>()

    const [goods, setGoods] = useState<InventoryGoodsDetail>({
        rowNum: 0,
        lctSeq: '',
        goodsId: '',
        goodsNm: '',
        centerId: '',
        centerNm: '',
        invnSeq: 0,
        goodsQty: 0,
        pckGoodsQty: 0,
        avlbGoodsQty: 0,
        gdslsUnitCd: '',
        specTxt: '',
        shlfYmd: '',
        shlfYmdFm: '',
        shlfRemainDay: 0,
        strgDay: 0,
        prchUnitAmt: 0,
        imageUri: '',
        shlfYn: '',
        lotNo: '',
        sysCd: '',
    })

    useEffect(() => {
        $codes(['RTRN_TYP'], true).then((result: CommonCode[][]) => {
            const [RTRN_TYP] = result
            RTRN_TYP[0].displayNm = t(tokens.common.select_return_type)
            RTRN_TYP[0].codeNm = t(tokens.common.select_return_type)
            setRtrnTypCd(
                // RTRN_TYP.filter((d) => d.codeId !== '' )
                RTRN_TYP,
            )
        })
    }, [$codes, $http])

    useMountEffect(() => {
        if (!props.data) return
        setGoods(props.data)
        formik.validateField('rtrnTypCd')
        formik.validateField('goodsQty')
    })
    const initialValues: GoodsReturnReq = {
        rtrnTypCd: '',
        goodsQty: undefined,
        rtrnTxt: '',
    }
    const formik = useFormik({
        initialValues,
        validationSchema: object({
            rtrnTypCd: string().required(t(tokens.common.select_return_type_is_required)),
            goodsQty: number()
                .required(t(tokens.common.please_enter_the_required_return_quantity))
                .min(1, t(tokens.inventory.enter_minimum_return_qty_of_1_or_more))
                .max(
                    goods.avlbGoodsQty,
                    `${t(tokens.common.returned_quantity_entered_is_the_returnable_quatity)}:` +
                        goods.avlbGoodsQty +
                        `${t(tokens.common.there_are_more)}`,
                ),
            rtrnTxt: string()
                .required(t(tokens.common.refund_reason_input_is_required))
                .max(1000, `${t(tokens.common.maximum_input)} Byte:` + 1000 + `${t(tokens.common.entered_more_than_allowed)}`),
        }),
        onSubmit: async (param: GoodsReturnReq) => {
            if (isEmpty(goods) || isEmpty(goods.lctSeq)) {
                $toast({ title: t(tokens.common.accept), text: t(tokens.inventory.no_items_to_return), icon: 'warning' })
                return
            }
            const isConfirm = await $alert({
                icon: 'info',
                title: t(tokens.common.supplier_return),
                text:
                    goods.goodsNm +
                    `${t(tokens.common.quantity_of_goods)}` +
                    param.goodsQty +
                    `${t(tokens.common.will_you_process_the_return_of_items)}?`,
                isConfirm: true,
            })
            if (!isConfirm) return

            const data: any = {
                goodsQty: param.goodsQty,
                rtrnTypCd: param.rtrnTypCd,
                rtrnTxt: param.rtrnTxt,
                lctSeq: goods.lctSeq,
                goodsId: goods.goodsId,
                invnSeq: goods.invnSeq,
            }
            // console.log('onSubmit', data)
            await $http()
                .put('/inventory/goods-return', data)
                .then((result: any) => {
                    if (!result) return // null pass
                    if (result.succCd !== 'S') {
                        $toast({
                            title: t(tokens.common.fail),
                            icon: 'failure',
                        })
                        return
                    }

                    $toast({ title: t(tokens.common.success), text: t(tokens.inventory.return_processed), icon: 'success' })
                    props.onHide(true)
                })
        },
    })

    return (
        <>
            {goods && (
                <div className="grid">
                    <div className="col-12 flex font-bold mt-2">
                        <label className="text-color-blue text-lg required pl-2">반품 유형</label>
                    </div>
                    <div className="col-12 flex pt-0">
                        <Dropdown
                            id="rtrnTypCd"
                            value={formik.values.rtrnTypCd}
                            className="w-full p-selectbutton-sm"
                            onChange={formik.handleChange}
                            options={rtrnTypCd}
                            optionLabel="displayNm"
                            optionValue="codeId"
                            // style={{ minWidth: '180px', zIndex: 2 }}
                        />
                    </div>
                    <div className="col-12 flex pl-2 pt-0">
                        {formik.errors.rtrnTypCd ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.rtrnTypCd}
                            </span>
                        ) : null}
                    </div>
                    <div className="col-12 flex font-bold mt-2">
                        <label className="text-color-blue text-lg required pl-2">{t(tokens.inventory.return_qty)}</label>
                    </div>
                    <div className="col-12 flex pt-0">
                        <InputNumber
                            id="goodsQty"
                            min={0}
                            placeholder={t(tokens.common.please_enter_the_quantity_to_return)}
                            inputStyle={{ paddingLeft: '1rem' }}
                            className="w-full"
                            value={formik.values.goodsQty}
                            onChange={(e) => formik.setFieldValue('goodsQty', e.value)}
                            locale="ko-KR"
                        />
                    </div>
                    <div className="col-12 flex pl-2 pt-0">
                        {formik.errors.goodsQty ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.goodsQty}
                            </span>
                        ) : null}
                    </div>
                    <div className="col-12 flex font-bold mt-2 pt-0">
                        <label className="text-color-blue text-lg required pl-2">{t(tokens.inventory.return_reason)}</label>
                    </div>
                    <div className="col-12 flex mt-0 fc-row pt-0">
                        <InputTextarea
                            id="rtrnTxt"
                            maxLength={1000}
                            value={formik.values.rtrnTxt}
                            className="w-full"
                            rows={3}
                            onChange={formik.handleChange}
                            placeholder={t(tokens.common.please_enter_the_return_reason)}
                        />
                    </div>
                    <div className="col-12 flex pl-2 pt-0">
                        {formik.errors.rtrnTxt ? (
                            <span className="ml-1 mt-1 text-red-600" style={{ fontSize: '12px' }}>
                                {formik.errors.rtrnTxt}
                            </span>
                        ) : null}
                    </div>
                    <form className="col-12 flex pt-0" onSubmit={formik.handleSubmit}>
                        <div className="col-6 flex pl-0">
                            <Button
                                className="w-full p-button-secondary"
                                label={t(tokens.common.close)}
                                size="large"
                                onClick={() => props.onHide(false)}
                            />
                        </div>
                        <div className="col-6 flex pr-0">
                            <Button className="w-full p-button-success" label={t(tokens.common.complete)} size="large" type="submit" />
                        </div>
                    </form>
                </div>
            )}
        </>
    )
}

export default GoodsMove
